import React, { useContext, useEffect, useState } from 'react';
import { useDeepCompareEffect } from 'use-deep-compare';
import { useWeb3React } from '@web3-react/core';
import { mainContext } from '../../state';
import { convertToBiobit } from '../../utils';
import appCache from '../../utils/cache';
import Desktop from './Desktop';
import Mobile from './Mobile';
import Guide from './../../components/Guide/Guide';
import { RequestDetailsDesktopSteps, RequestDetailsMobileSteps } from '../../guides';
import BigNumber from 'bignumber.js';
import SearchFilter from '../../components/searchAndFilter/SearchFilter';
import isEqual from 'lodash/isEqual';

const RequestsList = () => {
	const { appState } = useContext(mainContext);
	const { account } = useWeb3React();
	const PAGE_SIZE = 5;
	const [requests, setRequests] = useState({});
	const [requestsCount, setRequestsCount] = useState(0);
	const [dailyContributors, setDailyContributors] = useState(0);
	const [isLoading, setLoading] = useState(true);
	const [currentPage, setCurrentPage] = useState(1);
	const defaultFilters = {
		q: '',
		orderBy: 'requestID',
		orderDirection: 'desc',
		bbitFilter: [],
		dateFilter: [],
		nearFinish: false,
		fulfilled: false,
		mostConfirmed: false,
	};

	const [searchResults, setSearchResults] = useState({
		params: defaultFilters,
		hasActiveFilters: false,
		data: [],
	});

	const applySearch = {
		order: function (orderBy, orderDirection) {
			setSearchResults((config) => ({
				...config,
				params: {
					...config.params,
					orderBy,
					orderDirection,
				},
			}));
		},
		bbitFilter: function (bbitFilter) {
			setSearchResults((config) => ({
				...config,
				params: {
					...config.params,
					bbitFilter,
				},
			}));
		},
		dateFilter: function (dateFilter) {
			setSearchResults((config) => ({
				...config,
				params: {
					...config.params,
					dateFilter,
				},
			}));
		},
		nearFinish: function (nearFinish) {
			setSearchResults((config) => ({
				...config,
				params: {
					...config.params,
					nearFinish,
				},
			}));
		},
		fulfilled: function (fulfilled) {
			setSearchResults((config) => ({
				...config,
				params: {
					...config.params,
					fulfilled,
				},
			}));
		},
		mostConfirmed: function (mostConfirmed) {
			setSearchResults((config) => ({
				...config,
				params: {
					...config.params,
					mostConfirmed,
				},
			}));
		},
		q: function (q) {
			setSearchResults((config) => ({
				...config,
				params: {
					...config.params,
					q,
				},
			}));
		},
		clear: function () {
			setSearchResults(() => ({
				params: {
					q: '',
					orderBy: 'requestID',
					orderDirection: 'desc',
					bbitFilter: [],
					dateFilter: [],
					nearFinish: false,
					fulfilled: 'All',
					mostConfirmed: false,
				},
				data: [],
			}));
		},
	};
	useDeepCompareEffect(() => {
		try {
			const { q, orderBy, orderDirection, bbitFilter, dateFilter, nearFinish, fulfilled, mostConfirmed } =
				searchResults.params;
			let results = Object.values(requests);

			if (q) {
				if (q !== '' && q.length >= 3) {
					results = results.filter((request) => {
						return request.title.toLowerCase().includes(q.toLowerCase());
					});
				} else {
					throw Error('Search query must be at least 3 characters long');
				}
			}
            if (Array.isArray(bbitFilter) && bbitFilter.length === 2) {
                const [minBBIT, maxBBIT] = bbitFilter;
                const minOk = Number.isFinite(+minBBIT) && +minBBIT >= 0;
                const maxOk = Number.isFinite(+maxBBIT) && +maxBBIT >= 0;
                if (minOk && maxOk) {
                    results = results.filter((request) => {
                        const total = new BigNumber(request.totalTokenPay);
                        return total.gte(+minBBIT) && total.lte(+maxBBIT);
                    });
                }
            }

            if (Array.isArray(dateFilter) && dateFilter.length === 2) {
                const [start, end] = dateFilter;
                const startOk = Number.isFinite(+start) && +start >= 0;
                const endOk = Number.isFinite(+end) && +end >= 0;
                if (startOk && endOk) {
                    if (+start === +end) {
                        results = results.filter((request) => {
                            return +request.timestamp >= +start && +request.timestamp <= +start + 86400;
                        });
                    } else {
                        results = results.filter((request) => {
                            return +request.timestamp >= +start && +request.timestamp <= +end;
                        });
                    }
                }
            }

			if (nearFinish) {
				results = results.filter((request) => {
					if (+request.totalContributorsRemaining === 0) return false;
					return Math.floor((+request.totalContributed / +request.totalContributors) * 100) >= 70;
				});
			}

			if (fulfilled === 'Fulfilled') {
				results = results.filter((request) => {
					if (+request.totalContributors === 0) return false;
					return +request.totalContributorsRemaining === 0;
				});
			} else if (fulfilled === 'Avalible') {
				results = results.filter((request) => {
					if (+request.totalContributors === 0) return false;
					return +request.totalContributorsRemaining !== 0;
				});
			} else {
				// when all is selected result are not filtered
			}

			if (mostConfirmed) {
				results = results.filter((request) => {
					if (+request.totalContributorsRemaining === 0) return false;
					return Math.floor((+request.totalContributed / +request.totalContributedCount) * 100) >= 70;
				});
			}
			if (orderBy) {
				results = results.sort((a, b) => {
					if (orderBy === 'bbit') {
						if (orderDirection === 'asc') {
							return a.totalTokenPay - b.totalTokenPay;
						} else {
							return b.totalTokenPay - a.totalTokenPay;
						}
					} else if (orderBy === 'requestID') {
						if (orderDirection === 'asc') {
							return a.requestID - b.requestID;
						} else {
							return b.requestID - a.requestID;
						}
					} else {
						return 0;
					}
				});
			}
			setSearchResults((config) => {
				return {
					...config,
					hasActiveFilters: !isEqual(defaultFilters, searchResults.params),
					data: results,
				};
			});

			setCurrentPage(1);
		} catch (error) {
			console.error(error);
		}
	}, [searchResults, isLoading]);

	useEffect(() => {
		if (appState.contract !== null) {
			// Check cache first
			const cachedOrderSize = appCache.get(account || 'anonymous', 'orderSize');
			if (cachedOrderSize) {
				setRequestsCount(cachedOrderSize);
			} else {
				// Use promise-based call instead of callback for Web3 v4 compatibility
				appState.contract.methods.orderSize().call()
					.then((result) => {
						// Cache the result
						appCache.set(account || 'anonymous', 'orderSize', result);
						setRequestsCount(result);
					})
					.catch((error) => {
						console.error('Error calling orderSize:', error.message || error);
						console.error('Contract address:', process.env.REACT_APP_ZARELA_CONTRACT_ADDRESS);
					});
			}

		// Use promise-based calls for Web3 v4 compatibility
		for (let i = 0; i < requestsCount; i++) {
			appState.contract.methods.Categories(i).call()
				.then((result) => {
					let categories = result[0];
					let businessCategory = result[1];

					if (+businessCategory === +process.env.REACT_APP_ZARELA_BUSINESS_CATEGORY) {
						// filter categories and only show Zarela requests
						return appState.contract.methods.orders(i).call()
							.then((orderResult) => {
								const requestTemplate = {
									requestID: orderResult[0],
									title: orderResult[1],
									description: orderResult[7],
									requesterAddress: orderResult[2],
									angelTokenPay: convertToBiobit(orderResult[3], false),
									laboratoryTokenPay: convertToBiobit(orderResult[4], false),
									totalTokenPay: convertToBiobit(new BigNumber(orderResult[3]).plus(orderResult[4]), false),
									totalContributors: orderResult[5], // total contributors required
									totalContributed: +orderResult[5] - +orderResult[8],
									totalContributorsRemaining: orderResult[8], // total contributors remaining (able to contribute)
									whitePaper: orderResult[6],
									timestamp: orderResult[10],
									categories,
									totalContributedCount: orderResult[9], // no of received signals
								};
								setRequests((requests) => ({
									...requests,
									[requestTemplate.requestID]: requestTemplate,
								}));
								if (i === +requestsCount - 1) setLoading(false);
							});
					}
				})
				.catch((error) => {
					console.error('Error in Categories or orders call:', error.message || error);
				});
		}
		}
	}, [appState.contract, requestsCount]);

	useEffect(() => {
		if (appState.contract) {
			// Check cache first
			const cachedDailyContributors = appCache.get(account || 'anonymous', 'dailyContributors');
			if (cachedDailyContributors) {
				setDailyContributors(cachedDailyContributors);
			} else {
				// Use promise-based call instead of callback for Web3 v4 compatibility
				appState.contract.methods.todayContributionsCount().call()
					.then((result) => {
						// Cache the result
						appCache.set(account || 'anonymous', 'dailyContributors', result);
						setDailyContributors(result);
					})
					.catch((error) => {
						console.error('Error calling todayContributionsCount:', error.message || error);
						console.error('Contract address:', process.env.REACT_APP_ZARELA_CONTRACT_ADDRESS);
					});
			}
		}
	}, [appState.contract]);

	return (
		<Guide steps={appState.isMobile ? RequestDetailsMobileSteps : RequestDetailsDesktopSteps} isLoading={isLoading}>
			{appState.isMobile ? (
				<Mobile
					{...{
						requests: searchResults.data,
						appState,
						isLoading,
						PAGE_SIZE,
						currentPage,
						setCurrentPage,
						searchBox: <SearchFilter {...{ requests, applySearch, searchResults }} />,
					}}
				/>
			) : (
				<Desktop
					{...{
						requests: searchResults.data,
						appState,
						account,
						dailyContributors,
						PAGE_SIZE,
						isLoading,
						currentPage,
						setCurrentPage,
						searchBox: <SearchFilter {...{ requests, applySearch, searchResults }} />,
					}}
				/>
			)}
		</Guide>
	);
};

export default RequestsList;
